import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
import GetLabel from "../language/GetLabel.tsx";
import { useItemList } from "../utils/hooks.ts";
import { loadAppropriateImages } from "../utils/image-utils.ts";

// The program page's "Watch Latest Episode" hero. It is just items[0] of the same episode list the
// side carousel renders — so rather than a second fetch, it rides the SAME SWR fetch: both mounts
// call useItemList with the program id, so SWR dedups to one request and the hero updates whenever
// the list refreshes. The server emits an empty #program-latest-episode mount (space reserved, no
// layout shift); this renders into it once the list resolves, and it fades in via CSS
// (see #program-latest-episode .show-latest-episode in cpac.css).

const ProgramFeaturedView = ({ programId }: { programId: string }) => {
  // Same call shape as the side carousel's ItemList → identical SWR key → one deduped fetch. The
  // carousel passes its watchdog `generation` (0 on the happy path); match it with 0 so the keys are
  // equal and SWR collapses both mounts onto a single request (same dedup contract as the live bar).
  const { list } = useItemList("itemList", "", undefined, 0, undefined, undefined, undefined, programId);
  useEffect(() => {
    loadAppropriateImages();
  });

  const item = list?.items?.[0];
  if (!item) return null; // nothing yet (loading) or an empty list ⇒ render nothing

  return (
    <div className="show-latest-episode">
      <div className="latest-episode-thumbnail">
        <img
          data-src={item.image}
          loading="eager"
          className="multi-thumb"
          alt={item.altImageTxt ?? ""}
          itemProp="thumbnailUrl"
        />
      </div>
      <div className="latest-episode-info">
        <div className="play-button-watch-title">
          <a tabIndex={-1} href={item.url}>
            <button name="play">
              <span className="screen-reader-text">
                <GetLabel name="PLAY" />
              </span>
            </button>
          </a>
          <h2>
            <a href={item.url}>
              <GetLabel name="WATCH_LATEST_EPISODE" />
            </a>
          </h2>
        </div>
        <span itemProp="name">{item.title}</span>
      </div>
    </div>
  );
};

export const renderProgramFeatured = (div: string) => {
  const root = document.getElementById(div);
  if (!root) return; // mount only emitted for has_episodes programs

  const programId = root.dataset["programId"];
  if (!programId) return;

  const holder = document.createElement("div");
  root.appendChild(holder);
  ReactDOM.createRoot(holder).render(
    <React.StrictMode>
      <ProgramFeaturedView programId={programId} />
    </React.StrictMode>
  );
};
